@if (loading.page) {
  <maximizer-loader type="legacy"></maximizer-loader>
} @else {
  <div class="pb-20 space-y-2 h-full">
    <div class="bg-neutral-5">
      <div class="flex space-x-2 justify-between p-2">
        <kendo-textbox
          id="outlook-timeline-ui-timeline-search"
          [placeholder]="'outlook.timeline.searchInTimeline' | translate"
          [clearButton]="true"
          (valueChange)="onSearchInput($event)"
          class="text-body"
        >
          <ng-template kendoTextBoxPrefixTemplate>
            <span
              class="fa-icons icon-14 icon-magnifying-glass text-neutral-60 ml-2"
            ></span>
          </ng-template>
        </kendo-textbox>

        <button
          kendoTooltip
          [callout]="false"
          [tooltipTemplate]="tooltipTemplate"
          kendoButton
          id="outlook-timeline-ui-timeline-filters"
          themeColor="secondary"
          (click)="toggleFilterPopup()"
          [attr.aria-label]="
            showFilterPopup
              ? ('outlook.filter.close' | translate)
              : ('outlook.filter.open' | translate)
          "
          [title]="
            showFilterPopup
              ? ('outlook.filter.close' | translate)
              : ('outlook.filter.open' | translate)
          "
        >
          <div class="flex gap-1 items-center">
            <span
              class="fa-icons icon-16 icon-filters"
              [class]="{
                'icon-regular': !showFilterPopup,
                'icon-solid': showFilterPopup,
              }"
            ></span>
            @if (!aiTimelineSummaryEnabled()) {
              <p class="text-body bold">
                {{ 'outlook.buttons.filters' | translate }}
              </p>
            }
            <span class="fa-icons icon-solid icon-caret-down icon-8"></span>
          </div>
        </button>

        @if (aiTimelineSummaryEnabled()) {
          <button
            kendoTooltip
            [callout]="false"
            [tooltipTemplate]="tooltipTemplate"
            kendoButton
            themeColor="secondary"
            iconClass="maximizer-icons icon-timeline-summary icon-18"
            [attr.aria-label]="'outlook.summarization.title' | translate"
            [title]="'outlook.summarization.title' | translate"
            (click)="openTimelineSummary()"
          ></button>
        }
      </div>

      @if (showFilterPopup) {
        <form
          [formGroup]="form.group"
          class="bg-neutral-5 flex gap-2 pt-0 pb-2 px-2 items-end"
        >
          <!-- User -->
          <kendo-formfield class="flex-1 min-w-0">
            <kendo-label class="text-body truncate">
              {{ 'outlook.timeline.filter.users' | translate }}
            </kendo-label>
            <kendo-multiselecttree
              #usersSelector
              kendoMultiSelectTreeExpandable
              kendoMultiSelectTreeSummaryTag
              [kendoMultiSelectTreeHierarchyBinding]="users"
              [formControlName]="'users'"
              textField="displayName"
              valueField="key"
              childrenField="items"
              [checkAll]="true"
              [clearButton]="false"
              [filterable]="true"
              (valueChange)="onDropdownFilterChange('users')"
              (open)="onDropdownFilterOpen('users')"
              (closed)="onDropdownFilterClose('users')"
              [popupSettings]="{ width: 275, popupClass: 'text-body' }"
              class="timeline no-actions text-body"
              [placeholder]="
                'outlook.timeline.filter.placeholder.users' | translate
              "
            >
              <ng-template kendoMultiSelectTreeGroupTagTemplate let-items>
                <ng-container
                  *ngTemplateOutlet="
                    itemCountTag;
                    context: {
                      $implicit: items,
                      total: users.length,
                      control: usersSelector,
                    }
                  "
                ></ng-container>
              </ng-template>

              <ng-template kendoMultiSelectTreeNodeTemplate let-item>
                <div class="inline-flex items-center h-6">
                  <span
                    class="avatar avatar-xs avatar-inserv-10"
                    [style.background-image]="item.key | avatar"
                    >{{ item.displayName | nameInitials }}</span
                  >
                  <span class="ml-1 text-body">{{ item.displayName }}</span>
                </div>
              </ng-template>
            </kendo-multiselecttree>
          </kendo-formfield>

          <!-- Type -->
          <kendo-formfield class="flex-1 min-w-0">
            <kendo-label class="text-body">
              {{ 'outlook.timeline.filter.timelineItems' | translate }}
            </kendo-label>
            <kendo-multiselecttree
              #typesSelector
              kendoMultiSelectTreeExpandable
              kendoMultiSelectTreeSummaryTag
              [kendoMultiSelectTreeHierarchyBinding]="typesList"
              [formControlName]="'interaction'"
              textField="name"
              valueField="id"
              childrenField="items"
              [checkAll]="true"
              [clearButton]="false"
              [filterable]="true"
              (valueChange)="onDropdownFilterChange('types')"
              (open)="onDropdownFilterOpen('types')"
              (closed)="onDropdownFilterClose('types')"
              [popupSettings]="{ width: 275, popupClass: 'text-body' }"
              class="timeline no-actions text-body"
              [placeholder]="
                'outlook.timeline.filter.placeholder.timelineItems' | translate
              "
            >
              <ng-template kendoMultiSelectTreeGroupTagTemplate let-items>
                <ng-container
                  *ngTemplateOutlet="
                    itemCountTag;
                    context: {
                      $implicit: items,
                      total: typesList.length,
                      control: typesSelector,
                    }
                  "
                ></ng-container>
              </ng-template>

              <ng-template kendoMultiSelectTreeNodeTemplate let-item>
                <div
                  id="outlook-timeline-ui-timeline-filter{{ item.tag }}"
                  class="flex items-center text-body h-6"
                >
                  {{ item.name }}
                </div>
              </ng-template>
            </kendo-multiselecttree>
          </kendo-formfield>
        </form>
      }
    </div>

    @if (loading.results) {
      <div class="flex flex-col gap-2 px-4">
        <maximizer-outlook-skeleton-timeline></maximizer-outlook-skeleton-timeline>
        <maximizer-outlook-skeleton-timeline
          class="hidden h-sm:block"
        ></maximizer-outlook-skeleton-timeline>
      </div>
    } @else if (!importantNotes && !groupedTimeline) {
      <div class="flex flex-col items-center justify-center gap-4 h-full">
        <img
          class="w-[80px]"
          [src]="`${cdn}/assets/images/outlook/timeline-emptystate.png`"
          alt="{{ 'outlook.timeline.emptyState' | translate }}"
        />
        <span class="text-body">{{
          'outlook.timeline.emptyState' | translate
        }}</span>
      </div>
    } @else {
      <div class="px-2 space-y-2">
        @if (importantNotes) {
          <div
            class="rounded border border-neutral-20 flex flex-col divide-y divide-neutral-30 bg-white"
          >
            <div class="flex flex-row justify-center items-center">
              <span class="text-body bold p-2"
                >{{ 'outlook.timeline.importantNotes' | translate }}
              </span>
              <span class="text-body text-neutral-70"
                >{{ importantNotes.length }}
                {{ 'outlook.timeline.items' | translate }}</span
              >
            </div>
            @for (event of importantNotes; track $index) {
              <div class="flex flex-col">
                <maximizer-outlook-timeline-card
                  [event]="event"
                ></maximizer-outlook-timeline-card>
              </div>
            }
          </div>
        }
        @for (item of groupedTimeline; track $index) {
          <div
            class="rounded border border-neutral-20 flex flex-col divide-y divide-neutral-30 bg-white"
            (click)="toggleFilterPopup(false)"
          >
            <div class="flex justify-center text-center p-2 gap-1">
              <span class="text-body bold"
                >{{ item.date | date: 'MMMM yyyy' }}
              </span>
              <span class="text-body text-neutral-70"
                >{{ item.events.length }}
                {{ 'outlook.timeline.items' | translate }}</span
              >
            </div>
            @for (event of item.events; track $index) {
              <div class="flex flex-col">
                <maximizer-outlook-timeline-card
                  [event]="event"
                ></maximizer-outlook-timeline-card>
              </div>
            }
          </div>
        }

        @if (nextPageKey) {
          <div class="text-center pt-2">
            @if (loading.more) {
              <kendo-loader type="pulsing" themeColor="primary"></kendo-loader>
            } @else {
              <button
                kendoButton
                themeColor="primary"
                fillMode="flat"
                (click)="showMore()"
              >
                {{ 'outlook.buttons.showMore' | translate }}
              </button>
            }
          </div>
        }
      </div>
    }
  </div>
}

<!-- Dropdown summary tag template -->
<ng-template
  #itemCountTag
  kendoMultiSelectTreeGroupTagTemplate
  let-items
  let-total="total"
  let-control="control"
>
  <ng-template #selectionTooltip>
    <div class="text-body">
      @for (item of items; track $index) {
        <p [class.mt-1]="$index > 0">{{ item?.displayName ?? item?.name }}</p>
      }
    </div>
  </ng-template>

  <div
    kendoTooltip
    [popupTarget]="viewContainerRef.element"
    [tooltipTemplate]="selectionTooltip"
    filter=".count"
    position="bottom"
    [showOn]="items.length > 1 && !control?.isOpen ? 'hover' : 'none'"
    [callout]="false"
    class="flex items-center min-w-0 text-body"
  >
    @if (items.length > 1) {
      @if (items.length === total) {
        <span class="count text-body truncate min-w-0">{{
          'outlook.filter.all' | translate
        }}</span>
      } @else {
        <span class="count text-body truncate min-w-0">{{
          'outlook.filter.selection' | translate: { count: items.length }
        }}</span>
      }
    } @else if (items.length === 1) {
      <span class="count text-body truncate min-w-0">{{
        items[0]?.displayName ?? items[0]?.name
      }}</span>
    }
    <span class="k-font-icon k-i-caret-alt-down ml-0.5 flex-shrink-0"></span>
  </div>
</ng-template>

<maximizer-outlook-notification
  #notification
  entryId="timeline"
  [closable]="true"
>
</maximizer-outlook-notification>

<ng-template #tooltipTemplate let-anchor>
  <p class="caption text-center text-neutral-90 whitespace-nowrap">
    {{ anchor.nativeElement.getAttribute('data-title') }}
  </p>
</ng-template>

@if (timelineSummaryShown()) {
  <maximizer-outlook-ai-timeline-summary
    [timelineSummaryArgs]="timelineSummaryArgs"
    (closeDialog)="timelineSummaryShown.set(false)"
  ></maximizer-outlook-ai-timeline-summary>
}
